import { useState, useEffect, useCallback } from 'react';
import * as api from '../services/api';

/**
 * Hook to manage user favorite channels.
 * Exposes: { favorites, loading, error, addFavorite, removeFavorite, bulkRemoveFavorites, refresh }
 */
export function useFavorites() {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const normalize = (res) => {
    if (!res) return [];
    if (Array.isArray(res)) return res;
    // common envelope shapes
    if (Array.isArray(res.data)) return res.data;
    if (Array.isArray(res.favorites)) return res.favorites;
    if (Array.isArray(res.items)) return res.items;
    return [];
  };

  const fetchFavorites = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.request('/api/favorites');
      const list = normalize(res?.data ? res.data : res);
      /* c8 ignore next */
      setFavorites(list || []);
      return list;
    } catch (err) {
      /* c8 ignore next */
      setError(err.message || 'Failed to load favorites');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFavorites();
  }, [fetchFavorites]);

  const refresh = useCallback(() => fetchFavorites(), [fetchFavorites]);

  const addFavorite = useCallback(async (channelName, channelDisplayName) => {
    setError(null);
    // optimistic entry
    const optimistic = {
      channel_name: String(channelName).toLowerCase(),
      channel_display_name: channelDisplayName || channelName,
      added_at: new Date().toISOString(),
      _optimistic: true,
    };

    setFavorites((prev) => [optimistic, ...prev]);
    setLoading(true);
    try {
      const created = await api.request('/api/favorites', {
        method: 'POST',
        body: JSON.stringify({ channelName: optimistic.channel_name, channelDisplayName: optimistic.channel_display_name }),
      });

      // API may return { data: { ... } } or the object directly
      const createdObj = created?.data ?? created?.favorite ?? created;

      // Replace optimistic entry with server object when possible
      setFavorites((prev) => {
        const withoutOpt = prev.filter((f) => !f._optimistic);
        const newList = createdObj ? [createdObj, ...withoutOpt] : prev;
        return newList;
      });

      return createdObj || optimistic;
    } catch (err) {
      setError(err.message || 'Failed to add favorite');
      // revert optimistic
      setFavorites((prev) => prev.filter((f) => !f._optimistic));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const removeFavorite = useCallback(async (channelName) => {
    setError(null);
    const name = String(channelName).toLowerCase();
    // optimistic removal
    const prev = favorites;
    setFavorites((cur) => cur.filter((f) => (f.channel_name || f.channelName || '').toLowerCase() !== name));
    setLoading(true);
    try {
      await api.request(`/api/favorites/${encodeURIComponent(name)}`, { method: 'DELETE' });
      return true;
    } catch (err) {
      setError(err.message || 'Failed to remove favorite');
      // revert
      setFavorites(prev);
      return false;
    } finally {
      setLoading(false);
    }
  }, [favorites]);

  const bulkRemoveFavorites = useCallback(async (channelNames = []) => {
    setError(null);
    const names = (channelNames || []).map((n) => String(n).toLowerCase());
    const prev = favorites;
    setFavorites((cur) => cur.filter((f) => !names.includes((f.channel_name || f.channelName || '').toLowerCase())));
    setLoading(true);
    try {
      await api.request('/api/favorites/bulk', { method: 'DELETE', body: JSON.stringify({ channelNames: names }) });
      return true;
    } catch (err) {
      setError(err.message || 'Failed to remove favorites');
      setFavorites(prev);
      return false;
    } finally {
      setLoading(false);
    }
  }, [favorites]);

  return {
    favorites,
    loading,
    error,
    addFavorite,
    removeFavorite,
    bulkRemoveFavorites,
    refresh,
  };
}

export default useFavorites;
